import { useEffect, useMemo, useState } from 'react'
import { Plus, X } from 'lucide-react'
import type { Quote } from '@shared/types'
import { useStore } from '../../store'
import { api } from '../../lib/api'
import { useExtendedQuotes, useLiveQuotes, useMarketSession } from '../../hooks/useMarketData'
import { displaySymbol } from '../../lib/symbol'
import { dirClass, fmtPct, fmtPrice } from '../../lib/format'
import { cn, pollWhileVisible } from '../../lib/utils'
import { SymbolSearch } from './SymbolSearch'

/**
 * Watchlist: user-curated symbols they track (no shares). Add via search, remove with ×,
 * click a row to open the full symbol panel. Prices are live (WS) with delayed fallback —
 * same sourcing as the holdings table. Backed by the watchlist table + watchlist_* AI tools.
 */
export function WatchlistCard() {
  const watchlist = useStore((s) => s.watchlist)
  const addToWatchlist = useStore((s) => s.addToWatchlist)
  const removeFromWatchlist = useStore((s) => s.removeFromWatchlist)
  const openSymbol = useStore((s) => s.openSymbol)
  const [quotes, setQuotes] = useState<Record<string, Quote>>({})
  const [adding, setAdding] = useState(false)

  const session = useMarketSession()
  const live = useLiveQuotes(watchlist)
  const extended = useExtendedQuotes(watchlist, session)
  const key = watchlist.join(',')

  useEffect(() => {
    if (!watchlist.length) {
      setQuotes({})
      return
    }
    let alive = true
    const load = () =>
      api
        .quotes(watchlist)
        .then((qs) => {
          if (!alive) return
          const m: Record<string, Quote> = {}
          for (const q of qs) m[q.symbol] = q
          setQuotes(m)
        })
        .catch(() => {})
    const stop = pollWhileVisible(load, 60_000)
    return () => {
      alive = false
      stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const rows = useMemo(
    () =>
      watchlist.map((sym) => {
        const lv = live[sym]
        const ext = extended[sym]
        const q = quotes[sym]
        const price = lv?.price ?? ext?.price ?? q?.price ?? null
        // Outside regular hours measure vs the extended quote's regular close.
        const prevClose = (session !== 'regular' ? ext?.prevClose : undefined) ?? q?.previousClose ?? null
        const chgPct = price != null && prevClose ? ((price - prevClose) / prevClose) * 100 : q?.changePct ?? null
        return { sym, price, chgPct, ext: session !== 'regular' && !!ext }
      }),
    [watchlist, quotes, live, extended, session],
  )

  return (
    // NOTE: no `overflow-hidden` here — the add-search dropdown must be able to extend
    // past this (short) card without being clipped.
    <div className="card">
      <div className="flex items-center justify-between border-b border-[var(--line-soft)] px-3.5 py-2.5">
        <span className="text-[13px] font-semibold text-fg">Watchlist</span>
        <button
          onClick={() => setAdding((a) => !a)}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] text-fg-subtle hover:text-fg"
        >
          {adding ? <X size={13} /> : <Plus size={13} />} {adding ? 'Cancel' : 'Add'}
        </button>
      </div>
      {adding && (
        <div className="border-b border-[var(--line-soft)] p-3">
          <SymbolSearch
            value=""
            onSelect={(s) => {
              void addToWatchlist(s)
              setAdding(false)
            }}
            placeholder="Add symbol or company"
          />
        </div>
      )}
      {watchlist.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12.5px] text-fg-subtle">
          Nothing tracked yet — add symbols you want to keep an eye on, or just tell Libera “add NVDA to my watchlist”.
        </div>
      ) : (
        <div>
          {rows.map((r) => (
            <div
              key={r.sym}
              className="group flex cursor-pointer items-center gap-2 border-t border-[var(--separator)] px-3.5 py-2 first:border-t-0 hover:bg-[rgba(255,255,255,0.018)]"
              onClick={() => openSymbol(r.sym)}
            >
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg">
                {displaySymbol(r.sym)}
                {r.ext && (
                  <span className="ml-1.5 align-middle rounded bg-[var(--color-inset)] px-1 text-[9px] font-semibold uppercase text-fg-subtle">
                    {session === 'pre' ? 'PRE' : 'AH'}
                  </span>
                )}
              </span>
              <span className="num text-[12.5px] text-fg-muted">{r.price != null ? fmtPrice(r.price, '$') : '—'}</span>
              <span className={cn('num w-16 text-right text-[12px]', dirClass(r.chgPct ?? 0))}>
                {r.chgPct != null ? fmtPct(r.chgPct) : '—'}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  void removeFromWatchlist(r.sym)
                }}
                className="opacity-0 transition-opacity group-hover:opacity-100"
                title="Remove from watchlist"
              >
                <X size={13} className="text-fg-faint hover:text-down" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
